/**
 * "Recent changes" / audit strip for tenant branding, image upload,
 * and custom-domain events. Filters `audit_logs` by known entity/action
 * types and offers a CSV export.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Download, History, ShieldCheck, Image as ImageIcon, Palette, RefreshCw } from "lucide-react";
import { toast } from "sonner";

type Row = {
  id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  actor_id: string | null;
  metadata: any;
  created_at: string;
};

const ACTIONS = [
  "tenant_branding.created",
  "tenant_branding.updated",
  "tenant_branding.deleted",
  "tenant_domain.verified",
  "brand_asset.uploaded",
];

export function BrandingAuditPanel({ tenantSlug }: { tenantSlug?: string }) {
  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ["branding-audit", tenantSlug ?? "__all"],
    queryFn: async () => {
      let q = supabase
        .from("audit_logs")
        .select("id, action, entity_type, entity_id, actor_id, metadata, created_at")
        .in("action", ACTIONS)
        .order("created_at", { ascending: false })
        .limit(200);
      if (tenantSlug) q = q.contains("metadata", { slug: tenantSlug });
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const filtered = useMemo(() => {
    if (!tenantSlug) return rows;
    return rows.filter((r) => (r.metadata?.slug ?? "") === tenantSlug);
  }, [rows, tenantSlug]);

  const exportCsv = () => {
    if (!filtered.length) return toast.error("Nothing to export.");
    const headers = ["created_at", "action", "slug", "entity_id", "actor_id", "summary", "metadata"];
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [headers.join(",")];
    for (const r of filtered) {
      lines.push([
        r.created_at,
        r.action,
        r.metadata?.slug ?? "",
        r.entity_id ?? "",
        r.actor_id ?? "",
        summarize(r),
        JSON.stringify(r.metadata ?? {}),
      ].map(esc).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tricampus-brand-audit-${tenantSlug ?? "all"}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} rows`);
  };

  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <History className="h-4 w-4 text-primary" /> Recent brand & domain changes
          {tenantSlug && <Badge variant="outline" className="text-[10px]">{tenantSlug}</Badge>}
        </div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" variant="outline" onClick={exportCsv}>
            <Download className="mr-1 h-3.5 w-3.5" />CSV
          </Button>
        </div>
      </div>
      {isLoading ? (
        <div className="py-6 text-center text-xs text-muted-foreground">Loading…</div>
      ) : !filtered.length ? (
        <div className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">
          No branding or domain changes recorded yet.
        </div>
      ) : (
        <ScrollArea className="h-[320px] pr-2">
          <ol className="relative space-y-2 border-l pl-4">
            {filtered.map((r) => {
              const Icon = iconFor(r.action);
              return (
                <li key={r.id} className="relative">
                  <span className="absolute -left-[22px] top-1 grid h-4 w-4 place-items-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-2.5 w-2.5" />
                  </span>
                  <div className="rounded-md border bg-card p-2 text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary" className="text-[10px]">{r.action.replace("tenant_", "").replace("brand_", "")}</Badge>
                      {r.metadata?.slug && <span className="font-mono text-[11px]">{r.metadata.slug}</span>}
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px]">{summarize(r)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </ScrollArea>
      )}
    </Card>
  );
}

function iconFor(action: string) {
  if (action.startsWith("tenant_domain")) return ShieldCheck;
  if (action.startsWith("brand_asset")) return ImageIcon;
  return Palette;
}

function summarize(r: Row): string {
  const m = r.metadata ?? {};
  if (r.action === "tenant_domain.verified") return `Rechecked ${m.host} → ${m.status} · SSL ${m.ssl}`;
  if (r.action === "brand_asset.uploaded") return `Uploaded ${m.kind}${m.subKey ? ` (${m.subKey})` : ""}`;
  if (r.action === "tenant_branding.created") return `Tenant "${m.name ?? m.slug}" created${m.is_published ? " and published" : ""}`;
  if (r.action === "tenant_branding.deleted") return `Deleted tenant ${m.slug}`;
  if (r.action === "tenant_branding.updated") {
    const keys = Object.keys(m.changed_fields ?? {});
    if (!keys.length) return "Saved without changes";
    return `Changed ${keys.slice(0, 5).join(", ")}${keys.length > 5 ? ` +${keys.length - 5} more` : ""}`;
  }
  return r.action;
}
